import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';

interface PrivateRouteProps {
  children: React.ReactNode;
}

/** Keep app routes out of search results; in-app nav links still work. */
const NoIndex: React.FC = () => (
  <Helmet>
    <meta name="robots" content="noindex, nofollow" />
    <meta name="googlebot" content="noindex, nofollow" />
  </Helmet>
);

/**
 * PrivateRoute - requires login. Also marks the route noindex for SEO.
 */
export const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <>
        <NoIndex />
        <div className="min-h-screen flex items-center justify-center bg-dark-background">
          <div className="text-center">
            <div className="loading-spinner mx-auto mb-4"></div>
            <p className="text-dark-textSecondary">Verifying login status...</p>
          </div>
        </div>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <NoIndex />
        <Navigate to="/auth" state={{ from: location }} replace />
      </>
    );
  }

  return (
    <>
      <NoIndex />
      {children}
    </>
  );
};
